// Gazelle in the demo: no model, no key. A few questions answered from the
// demo's own data, with the same reply shape as the gazelle Edge Function
// (text, proposal cards, reads), so a design review sees the real flow:
// read, propose, approve, journal. Answers follow the demo's state, so a
// home reminded or an order assigned is not proposed twice.
import { demoOrders, demoOverview } from './demo';
import { demoDevices, demoHomes } from './demo-homes';
import { demoActions } from './demo-actions';
import { demoFinance } from './demo-money';
import { demoMarketers } from './demo-brands';
import { fmtN } from './format';
import type { GzReply, GzTurn } from './types';

const first = (name: string) => name.split(' ')[0];
const and = (xs: string[]) => (xs.length < 2 ? xs.join('') : xs.slice(0, -1).join(', ') + ' et ' + xs[xs.length - 1]);

export async function demoAsk(turns: GzTurn[]): Promise<GzReply> {
  const q = (turns[turns.length - 1]?.content ?? '').toLowerCase();
  const orders = demoActions.apply(demoOrders());

  if (/vide|semaine|bas|rappel/.test(q) && !/kit|pay/.test(q)) {
    const homes = demoHomes().filter((h) => h.connected && h.pace && h.pace.days_left < 7).sort((a, b) => a.pace!.days_left - b.pace!.days_left);
    const open = homes.filter((h) => h.open_order);
    const reminded = new Set((await demoActions.journal()).filter((j) => j.action === 'remind').map((j) => j.customer));
    const todo = homes.filter((h) => !h.open_order && (h.home?.level ?? 100) < 20 && !reminded.has(h.name));
    const list = homes.slice(0, 6).map((h) => `${h.name} (${h.quartier}) · ${h.home?.level ?? '—'} %, vide dans ${Math.max(0, Math.round(h.pace!.days_left))} j${h.open_order ? ', commande en cours' : ''}`);
    return {
      text: `${homes.length} foyers seront vides d’ici 7 jours. ${open.length} ont déjà une commande.${todo.length ? ` ${and(todo.map((h) => h.name))} n’ont rien commandé et sont sous 20 %.` : ' Tous les foyers sous 20 % ont déjà commandé ou reçu le rappel.'}\n\n${list.map((l) => '· ' + l).join('\n')}\n\nSource : niveau des capteurs et consommation des 30 derniers jours, commandes ouvertes`,
      proposals: todo.length ? [{ kind: 'remind', customer_ids: todo.map((h) => h.customer_id), summary: `Envoyer « Niveau de gaz bas, commandez en un geste » à ${and(todo.map((h) => h.name))}. Rien n’est commandé à leur place.` }] : [],
      reads: ['list_homes', 'get_journal'],
    };
  }

  if (/affect|attente|prendre|proche|livreur/.test(q)) {
    const o = orders.find((x) => x.status === 'placed');
    if (!o) return { text: 'Aucune commande n’attend de livreur en ce moment. Toutes les commandes du jour sont affectées.\n\nSource : commandes du jour', proposals: [], reads: ['list_orders'] };
    const riders = demoActions.riders(o.id);
    const free = riders.filter((r) => r.live && !r.busy);
    const best = free.find((r) => !r.can_install) ?? free[0];
    const mins = Math.max(1, Math.round((Date.now() - Date.parse(o.created_at)) / 60000));
    const busy = riders.filter((r) => r.busy).map((r) => first(r.name));
    return {
      text: best
        ? `${o.customer.name} (${o.customer.quartier}) attend depuis ${mins} min, commande ${o.ref}. ${best.name} est libre et en ligne : c’est le meilleur choix${best.rating ? ` (★ ${String(best.rating).replace('.', ',')})` : ''}.${busy.length ? ` ${and(busy)} ${busy.length > 1 ? 'sont' : 'est'} déjà en course.` : ''}${free.some((r) => r.can_install && r.id !== best.id) ? ' Je garde Alain, qui installe les kits, pour une installation.' : ''}\n\nSource : commandes du jour, livreurs de l’agence et leur position`
        : `${o.customer.name} attend depuis ${mins} min, mais aucun livreur n’est libre et en ligne.\n\nSource : livreurs de l’agence`,
      proposals: best ? [{ kind: 'assign', order_id: o.id, rider_id: best.id, summary: `Affecter ${o.ref} à ${best.name}. Il reçoit la course sur MyGaz Pro et ${o.customer.name} voit son arrivée.` }] : [],
      reads: ['list_orders', 'riders_for'],
    };
  }

  if (/kit|pay|argent|encaiss|finance/.test(q)) {
    const f = demoFinance(7);
    const days = f.days.slice(-7);
    const kits = days.reduce((n, d) => n + d.kits, 0);
    const xaf = days.reduce((n, d) => n + d.kit_xaf, 0);
    const pend = f.payments.filter((p) => p.kind === 'kit' && p.status !== 'completed' && p.status !== 'failed' && p.status !== 'rejected');
    return {
      text: `${kits} kits payés ces 7 derniers jours, soit ${fmtN(xaf)} XAF par mobile money.${pend.length ? ` ${pend.length} paiement${pend.length > 1 ? 's' : ''} attend${pend.length > 1 ? 'ent' : ''} encore la confirmation du payeur : rien n’est encaissé tant que ce n’est pas confirmé.` : ''}${f.refunds_due.n ? ` ${f.refunds_due.n} remboursement${f.refunds_due.n > 1 ? 's' : ''} à faire, ${fmtN(f.refunds_due.amount)} XAF.` : ''}\n\nJe ne touche pas à l’argent : un remboursement se fait depuis Finances.\n\nSource : paiements pawaPay, type kit`,
      proposals: [], reads: ['get_finance'],
    };
  }

  if (/muet|boîtier|boitier|passerelle|appareil/.test(q)) {
    const silent = demoDevices().kits.filter((k) => !k.online);
    return {
      text: silent.length
        ? `${silent.length} passerelle${silent.length > 1 ? 's' : ''} ne répond${silent.length > 1 ? 'ent' : ''} plus : ${and(silent.map((k) => `${k.name} (${k.quartier})`))}. La cause la plus fréquente est une prise débranchée ou une coupure ; un appel au foyer suffit souvent avant une visite.\n\nSource : état des boîtiers, dernières mesures`
        : 'Tous les boîtiers répondent.\n\nSource : état des boîtiers',
      proposals: [], reads: ['get_devices'],
    };
  }

  if (/marque|stock|bouteille/.test(q)) {
    const m = demoMarketers();
    const rows = m.brands.map((b) => ({ b, gap: b.stats.due7 - b.stock })).sort((x, y) => y.gap - x.gap);
    const short = rows.filter((r) => r.gap > 0);
    return {
      text: `${short.length ? `${and(short.map((r) => r.b.name))} ${short.length > 1 ? 'manqueront' : 'manquera'} de bouteilles connectées cette semaine.` : 'Aucune marque ne manque cette semaine.'} Je compte les bouteilles que les capteurs voient se vider sous 7 jours, face aux pleines déjà en station.\n\n${rows.map((r) => `· ${r.b.name} : ${r.b.stats.due7} à recharger, ${r.b.stock} pleines en station`).join('\n')}\n\nSource : niveaux des capteurs, stock des stations par marque`,
      proposals: [], reads: ['get_marketers'],
    };
  }

  const ov = demoOverview();
  return {
    text: `Aujourd’hui : ${ov.today.orders} commandes, ${ov.today.delivered} livrées. ${ov.homes.connected} foyers connectés, ${ov.homes.low} sous 20 % dont ${ov.homes.low_no_order} sans commande. ${ov.riders.live} livreurs en service.\n\nDans la démo, je réponds à quelques questions préparées. Avec la clé posée, je lis toutes tes données et je réponds à tout.\n\nSource : aperçu du jour`,
    proposals: [], reads: ['get_overview'],
  };
}
